import type {ConfiguratorVariant} from "#pricing/types/pricing-types"
import {configuratorVariantSchema} from "#pricing/types/pricing-types"
import {PricingNode, registerPricingGraphNode} from "#pricing/pricing-node"
import type {PricingContext} from "#pricing/pricing-context"
import type {Evaluator, GraphNodeDefinition, ParameterValue, Version} from "@cm/graph-v3"
import {parameterValue, versionChain} from "@cm/graph-v3"
import {z} from "zod/v4-mini"

export type ConfigurationGroupNodeParametersV0 = {
    groupId: string
    currentVariantId: string
    allVariantIds: string[] | undefined
}

export type ConfigurationGroupNodeParameters = {
    groupId: string
    groupName: string
    currentVariant: ParameterValue<ConfiguratorVariant, PricingContext>
    allVariants: ConfiguratorVariant[] | undefined
}

const v0: Version<ConfigurationGroupNodeParametersV0, ConfigurationGroupNodeParameters> = {
    toNextVersion: (parameters) => {
        const groupId = parameters.groupId
        const groupName = ""
        const toNamed = (variantId: string): ConfiguratorVariant => ({
            groupId,
            groupName,
            variantId,
            variantName: "",
        })
        return {
            groupId,
            groupName,
            currentVariant: toNamed(parameters.currentVariantId),
            allVariants: parameters.allVariantIds?.map(toNamed),
        }
    },
}

const configurationGroupNodeDefinition = {
    nodeClass: "ConfigurationGroupNode",
    validation: {
        parameters: z.object({
            groupId: z.string(),
            groupName: z.string(),
            currentVariant: parameterValue(configuratorVariantSchema),
            allVariants: z.union([z.array(configuratorVariantSchema), z.undefined()]),
        }),
        returns: configuratorVariantSchema,
    },
    versionChain: versionChain([v0]),
} satisfies GraphNodeDefinition<ConfiguratorVariant, ConfigurationGroupNodeParameters>

@registerPricingGraphNode
export class ConfigurationGroupNode extends PricingNode<ConfiguratorVariant, ConfigurationGroupNodeParameters> {
    public override get definition(): GraphNodeDefinition<ConfiguratorVariant, ConfigurationGroupNodeParameters> {
        return configurationGroupNodeDefinition
    }

    override run = async (evaluator: Evaluator<PricingContext>) => {
        return await evaluator.get(this.parameters.currentVariant)
    }

    setCurrentVariantId(variantId: string, variantName: string) {
        this.updateParameters({currentVariant: this.buildVariant(variantId, variantName)})
    }

    clearCurrentVariantId() {
        this.updateParameters({currentVariant: this.buildVariant("", "")})
    }

    addVariant(variantId: string, variantName: string) {
        const allVariants = this.parameters.allVariants ?? []
        if (allVariants.some((v) => v.variantId === variantId)) return
        this.updateParameters({allVariants: [...allVariants, this.buildVariant(variantId, variantName)]})
    }

    hasVariant(variantId: string): boolean {
        if (this.parameters.allVariants === undefined) return false
        return this.parameters.allVariants.some((v) => v.variantId === variantId)
    }

    getGroupId(): string {
        return this.parameters.groupId
    }

    private buildVariant(variantId: string, variantName: string): ConfiguratorVariant {
        return {
            groupId: this.parameters.groupId,
            groupName: this.parameters.groupName,
            variantId,
            variantName,
        }
    }
}
